import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  Calculate as CalculateIcon,
  MoreHoriz as OtherIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { SalesOrder } from '../../../../types/sales.types';
import {
  DialogZoomTransition,
  DIALOG_ZOOM_MS,
  dialogZoomSlotProps,
  dialogZoomDialogSx,
  dialogZoomPaperSx,
} from '../../../ui/DialogZoomTransition';
import { usePaymentModalLogic } from './PaymentModal.hooks';
import { colors } from '../../../../styles/theme/colors';
import * as S from './PaymentModal.styles';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import { PaymentMethod } from '../../../../api/models/PaymentMethod';
import PaymentAmountDialog from '../../shared/PaymentAmountDialog';
import Card from '@mui/material/Card';
import { useTabScopedOpen } from '../../../dashboard/TabPanelActiveContext';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  order: SalesOrder | null;
  onPaymentProcessed?: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  order,
  onPaymentProcessed,
}) => {
  const {
    payments,
    isLoading,
    isLoadingSummary,
    error,
    success,
    paymentSummary,
    remainingBalance,
    dashboardValues,
    remainingAfterPayment,
    deliverAllOnComplete,
    setDeliverAllOnComplete,
    deliverableCount,
    canAutoDeliverOnComplete,
    otherPaymentMethods,
    showOtherMethodsDialog,
    showPaymentAmountDialog,
    pendingPaymentMethod,
    editingPaymentId,
    handlePaymentMethodClick,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePaymentAmount,
    handleEditPayment,
    handleClosePaymentAmountDialog,
    handleProcessPayment,
    handleOtherMethodSelect,
    handleOpenOtherMethods,
    handleCloseOtherMethods,
    clearError,
    clearSuccess,
    getPaymentMethodIcon,
    getPaymentMethodName,
    getTotalPayments,
    formatCurrency,
    formatDate,
  } = usePaymentModalLogic({ order, onPaymentProcessed });

  const scopedOpen = useTabScopedOpen(open);

  if (!open || !order) return null;

  return (
    <Dialog
      open={scopedOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={DialogZoomTransition}
      transitionDuration={DIALOG_ZOOM_MS}
      slotProps={dialogZoomSlotProps}
      sx={dialogZoomDialogSx}
      PaperProps={{
        sx: dialogZoomPaperSx({
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(2, 6, 23, 0.18)',
          maxHeight: '90vh',
          margin: 'auto',
        }),
      }}
    >
      <DialogTitle
        sx={{
          padding: '16px 24px',
          borderBottom: 1,
          borderColor: 'divider',
          background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.25rem' }}>
            Procesar Pago: {order.orderCode}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              color: colors.grey[200],
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={clearSuccess}>
              {success}
            </Alert>
          )}

          {/* Dashboard cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <S.DashboardCardBlue variant="outlined">
                <CardContent sx={{ py: 2.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <S.DashboardIconBoxBlue>
                    <CalculateIcon fontSize="small" />
                  </S.DashboardIconBoxBlue>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4, color: '#2563eb' }}>
                      Total de venta
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: '#1d4ed8', lineHeight: 1.2 }}
                    >
                      {formatCurrency(Number(dashboardValues.orderTotal) || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </S.DashboardCardBlue>
            </Grid>
            <Grid item xs={12} md={4}>
              <S.DashboardCardGreen variant="outlined">
                <CardContent sx={{ py: 2.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <S.DashboardIconBoxGreen>
                    <PaymentIcon fontSize="small" />
                  </S.DashboardIconBoxGreen>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4 }} color="success.main">
                      Pagado
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'success.dark', lineHeight: 1.2 }}
                    >
                      {formatCurrency(Number(dashboardValues.totalPaid) || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </S.DashboardCardGreen>
            </Grid>
            <Grid item xs={12} md={4}>
              <S.DashboardCardRed variant="outlined">
                <CardContent sx={{ py: 2.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <S.DashboardIconBoxRed>
                    <MoneyIcon fontSize="small" />
                  </S.DashboardIconBoxRed>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 0.4 }} color="error">
                      Saldo Pendiente
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, color: 'error.dark', lineHeight: 1.2 }}
                    >
                      {formatCurrency(Number(dashboardValues.remaining) || 0)}
                    </Typography>
                  </Box>
                </CardContent>
              </S.DashboardCardRed>
            </Grid>
          </Grid>

          {/* Estado de Pagos */}
          <S.PaymentStatusBox>
            <S.PaymentStatusHeader>
              <CalculateIcon color="success" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'success.dark' }}>
                Estado de Pagos
              </Typography>
            </S.PaymentStatusHeader>
            <Box sx={{ p: 2 }}>
              {isLoadingSummary ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography>Cargando estado...</Typography>
                </Box>
              ) : paymentSummary ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Progreso de Pago
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={paymentSummary.completionPercentage}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 4,
                          '& .MuiLinearProgress-bar': { backgroundColor: 'success.main' },
                          backgroundColor: 'rgba(16,185,129,0.2)',
                        }}
                      />
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        {paymentSummary.completionPercentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Pagos Realizados
                    </Typography>
                    <Typography variant="body1">
                      {paymentSummary.paymentCount} pago
                      {paymentSummary.paymentCount !== 1 ? 's' : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Último Pago
                    </Typography>
                    <Typography variant="body1">
                      {paymentSummary.lastPaymentDate
                        ? formatDate(paymentSummary.lastPaymentDate as unknown as string)
                        : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    backgroundColor: alpha(
                      theme.palette.warning.main,
                      theme.palette.mode === 'dark' ? 0.18 : 0.08,
                    ),
                    border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                    borderRadius: '8px',
                    padding: '10px 14px',
                  })}
                >
                  <InfoIcon
                    sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }}
                  />
                  <Typography variant="body2" sx={{ color: 'warning.light', lineHeight: 1.5 }}>
                    No se pudo cargar el estado de pagos
                  </Typography>
                </Box>
              )}
            </Box>
          </S.PaymentStatusBox>

          <Grid container spacing={3}>
            {/* Payment Details */}
            <Grid item xs={12} md={8}>
              <S.SectionBox>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    pb: 1,
                  }}
                >
                  <S.SectionHeader>
                    <PaymentIcon color="primary" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#155e75' }}>
                      Métodos de Pago
                    </Typography>
                  </S.SectionHeader>
                </Box>
                <Box sx={{ p: 2 }}>
                  {/* Payment Method Buttons */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr 1fr 1fr 1fr', sm: 'repeat(4, 1fr)' },
                      gap: 1,
                      mb: 3,
                    }}
                  >
                    {[PAYMENT_METHODS.CASH, PAYMENT_METHODS.CARD, PAYMENT_METHODS.QR].map(
                      (method) => {
                        const Icon = getPaymentMethodIcon(method);
                        return (
                          <S.PaymentMethodButton
                            key={method}
                            variant="outlined"
                            size="small"
                            startIcon={<Icon />}
                            onClick={() => handlePaymentMethodClick(method)}
                          >
                            {getPaymentMethodName(method)}
                          </S.PaymentMethodButton>
                        );
                      },
                    )}
                    {/* Other Payment Methods Button */}
                    <S.PaymentMethodButton
                      variant="outlined"
                      size="small"
                      startIcon={<OtherIcon />}
                      onClick={handleOpenOtherMethods}
                      disabled={otherPaymentMethods.length === 0}
                      sx={{
                        opacity: otherPaymentMethods.length === 0 ? 0.5 : 1,
                        cursor: otherPaymentMethods.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Otro
                    </S.PaymentMethodButton>
                  </Box>

                  {/* Payments List */}
                  {payments.length === 0 ? (
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          theme.palette.mode === 'dark' ? 0.16 : 0.08,
                        ),
                        border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.4 : 0.35)}`,
                        borderRadius: '8px',
                        padding: '10px 14px',
                        mb: 2,
                      })}
                    >
                      <InfoIcon
                        sx={{ fontSize: 18, color: 'success.light', mt: '2px', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ color: 'success.light', lineHeight: 1.5 }}>
                        No hay pagos agregados. Seleccione un método de pago para agregar un pago.
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 3 }}>
                      {payments.map((payment) => {
                        const isCash = payment.method === PAYMENT_METHODS.CASH;
                        const IconComponent = getPaymentMethodIcon(payment.method);
                        return (
                          <Card
                            key={payment.id}
                            sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}
                          >
                            <CardContent>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Box
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                  >
                                    <IconComponent fontSize="small" color="primary" />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {getPaymentMethodName(payment.method)}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                                    {formatCurrency(payment.amount)}
                                  </Typography>
                                  {isCash &&
                                    payment.cashReceived != null &&
                                    payment.cashReceived > payment.amount && (
                                      <Typography variant="body2" color="text.secondary">
                                        Recibido: {formatCurrency(payment.cashReceived)}
                                        {' - '}
                                        Cambio:{' '}
                                        {formatCurrency(payment.cashReceived - payment.amount)}
                                      </Typography>
                                    )}
                                  {payment.paymentNotes && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {payment.paymentNotes}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditPayment(payment.id)}
                                  >
                                    Editar
                                  </Button>
                                  <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleRemovePayment(payment.id)}
                                  >
                                    Eliminar
                                  </Button>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </S.SectionBox>

              {/* Other Payment Methods Dialog */}
              <Dialog
                open={showOtherMethodsDialog}
                onClose={handleCloseOtherMethods}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Seleccionar Método de Pago</DialogTitle>
                <DialogContent>
                  {otherPaymentMethods.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hay otros métodos de pago disponibles.
                    </Typography>
                  ) : (
                    <List>
                      {otherPaymentMethods.map((method: PaymentMethod) => (
                        <ListItem key={method.id} disablePadding>
                          <ListItemButton onClick={() => handleOtherMethodSelect(method)}>
                            <ListItemIcon>
                              <PaymentIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={method.name}
                              secondary={method.description || method.code}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </DialogContent>
                <DialogActions>
                  <S.CancelButton onClick={handleCloseOtherMethods}>Cancelar</S.CancelButton>
                </DialogActions>
              </Dialog>

              {/* Payment Amount Dialog */}
              <PaymentAmountDialog
                open={showPaymentAmountDialog}
                onClose={handleClosePaymentAmountDialog}
                method={
                  pendingPaymentMethod ??
                  (editingPaymentId
                    ? (payments.find((p) => p.id === editingPaymentId)?.method ?? null)
                    : null)
                }
                editingPayment={
                  editingPaymentId
                    ? (payments.find((p) => p.id === editingPaymentId) ?? null)
                    : null
                }
                remainingBalance={remainingBalance - getTotalPayments()}
                onAdd={handleAddPayment}
                onUpdate={handleUpdatePaymentAmount}
                formatCurrency={formatCurrency}
                prefillRemainingAmount={false}
              />
            </Grid>

            {/* Payment Summary */}
            <Grid item xs={12} md={4}>
              <S.SectionBox>
                <S.SectionHeader>
                  <CalculateIcon color="info" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#155e75' }}>
                    Resumen del Pago
                  </Typography>
                </S.SectionHeader>

                <Box sx={{ p: 2 }}>
                  <Box sx={{ space: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Total pagado:</Typography>
                      <Typography fontWeight="600" color="primary.main">
                        {formatCurrency(getTotalPayments())}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Saldo Restante:</Typography>
                      <Typography
                        color={remainingAfterPayment > 0 ? 'error' : 'success.main'}
                        fontWeight="600"
                      >
                        {formatCurrency(remainingAfterPayment)}
                      </Typography>
                    </Box>
                  </Box>

                  {remainingAfterPayment > 0 && (
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        backgroundColor: alpha(
                          theme.palette.warning.main,
                          theme.palette.mode === 'dark' ? 0.18 : 0.08,
                        ),
                        border: `1px solid ${alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.45 : 0.35)}`,
                        borderRadius: '8px',
                        padding: '10px 14px',
                        mt: 2,
                      })}
                    >
                      <InfoIcon
                        sx={{ fontSize: 18, color: 'warning.light', mt: '2px', flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ color: 'warning.light', lineHeight: 1.5 }}>
                        Quedará un saldo pendiente de {formatCurrency(remainingAfterPayment)}
                      </Typography>
                    </Box>
                  )}

                  {remainingAfterPayment === 0 && payments.length > 0 && (
                    <Box
                      sx={(theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        backgroundColor: alpha(
                          theme.palette.success.main,
                          theme.palette.mode === 'dark' ? 0.16 : 0.08,
                        ),
                        border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.4 : 0.35)}`,
                        borderRadius: '8px',
                        padding: '10px 14px',
                        mt: 2,
                      })}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <InfoIcon
                          sx={{ fontSize: 18, color: 'success.light', mt: '2px', flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: 'success.light', lineHeight: 1.5 }}
                        >
                          ¡La venta quedará completamente pagada!
                        </Typography>
                      </Box>
                      {canAutoDeliverOnComplete && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={deliverAllOnComplete}
                              onChange={(_, checked) => setDeliverAllOnComplete(checked)}
                              disabled={isLoading}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Marcar como entregadas las {deliverableCount} pieza
                              {deliverableCount === 1 ? '' : 's'} disponible
                              {deliverableCount === 1 ? '' : 's'} en sucursal
                            </Typography>
                          }
                          sx={{ ml: 0.5, alignItems: 'flex-start' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </S.SectionBox>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5,
        })}
      >
        <S.CancelButton onClick={onClose} disabled={isLoading}>
          Cancelar
        </S.CancelButton>
        <S.ProcessButton
          variant="contained"
          onClick={handleProcessPayment}
          disabled={isLoading || payments.length === 0}
          startIcon={isLoading ? <CircularProgress size={20} /> : <PaymentIcon />}
        >
          {isLoading ? 'Procesando...' : 'Procesar pagos'}
        </S.ProcessButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal;
